import React, { useState, useEffect } from 'react'
import { Box, Typography, Button, IconButton, CircularProgress } from '@mui/material'
import { ReactComponent as ImageIcon } from '../../../../common/icons/image_icon.svg'
import DeleteIcon from '@mui/icons-material/Delete'
import { useUploadImageMutation } from '../../../../api/trainingTopics'
import useMutation from '../../../../hooks/useMutation'

const MAX_IMAGE_WIDTH = 460
const MAX_IMAGE_HEIGHT = 315

export default function ImageUploader({ imageUrl, onImageChange, onImageRemove, topicId, alignment = 'CENTER' }) {
  const [isHovering, setIsHovering] = useState(false)
  const [localImageUrl, setLocalImageUrl] = useState(imageUrl || '')
  const [error, setError] = useState('')
  const [imageContainerStyle, setImageContainerStyle] = useState({})

  const { submit: uploadImage, isLoading } = useMutation(useUploadImageMutation, {
    showSuccessToast: false
  })

  useEffect(() => {
    setLocalImageUrl(imageUrl || '')
    if (imageUrl) {
      const img = new Image()
      img.onload = () => {
        const naturalWidth = img.naturalWidth
        const naturalHeight = img.naturalHeight

        const displayHeight = MAX_IMAGE_HEIGHT
        let displayWidth = (naturalWidth / naturalHeight) * displayHeight

        setImageContainerStyle({
          width: `${displayWidth}px`,
          height: `${displayHeight}px`
        })
      }
      img.onerror = () => {
        setImageContainerStyle({
          width: `${MAX_IMAGE_WIDTH}px`,
          height: `${MAX_IMAGE_HEIGHT}px`
        })
      }
      img.src = imageUrl
    } else {
      setImageContainerStyle({
        width: `${MAX_IMAGE_WIDTH}px`,
        height: `${MAX_IMAGE_HEIGHT}px`
      })
    }
  }, [imageUrl])

  const handleImageUpload = async event => {
    const file = event.target.files[0]
    if (!file) return

    setError('')

    if (file.size > 5 * 1024 * 1024) {
      setError('File size exceeds 5MB limit')
      return
    }

    const supportedFormats = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!supportedFormats.includes(file.type)) {
      setError('Only JPEG, PNG, GIF, and WEBP formats are supported')
      return
    }

    const formData = new FormData()
    formData.append('fileUpload', file)

    try {
      const response = await uploadImage({
        topicId,
        body: formData
      })
      if (response && response.imageUrl) {
        setLocalImageUrl(response.imageUrl)
        onImageChange(response.imageUrl)
      } else {
        throw new Error('Invalid response format')
      }
    } catch (error) {
      console.error('Error uploading image:', error)
      setError('Failed to upload image. Please try again.')
    }
  }

  const handleRemoveImage = () => {
    setLocalImageUrl('')
    setError('')
    if (onImageRemove) {
      onImageRemove()
    }
  }

  const handleMouseEnter = () => {
    setIsHovering(true)
  }

  const handleMouseLeave = () => {
    setIsHovering(false)
  }

  let objectPositionValue = 'center center'
  if (alignment === 'LEFT') {
    objectPositionValue = 'left center'
  } else if (alignment === 'RIGHT') {
    objectPositionValue = 'right center'
  }

  return (
    <Box sx={styles.imageContainer}>
      {localImageUrl ? (
        <Box
          sx={{ ...styles.imagePreview, ...imageContainerStyle }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <img
            src={localImageUrl}
            alt='Module content'
            style={{ ...styles.image, objectPosition: objectPositionValue }}
            onError={() => console.error('Image failed to load:', localImageUrl)}
          />
          {isHovering && (
            <IconButton onClick={handleRemoveImage} sx={styles.deleteButton} aria-label='delete image'>
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ) : (
        <Box sx={styles.imagePlaceholder}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <>
              <ImageIcon style={styles.imageIcon} />
              <input
                accept='image/jpeg,image/png,image/gif,image/webp'
                style={{ display: 'none' }}
                id='image-upload'
                type='file'
                onChange={handleImageUpload}
              />
              <label htmlFor='image-upload'>
                <Button variant='outlined' component='span' sx={styles.uploadButton}>
                  Upload Image
                </Button>
              </label>
              <Typography variant='caption' color='text.secondary' sx={styles.maxSizeInfo}>
                Max file size: 5MB | Supported formats: JPEG, PNG, GIF, WEBP
              </Typography>
              {error && (
                <Typography variant='caption' color='error' sx={styles.errorText}>
                  {error}
                </Typography>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  )
}

const styles = {
  imageContainer: {
    width: '100%',
    marginBottom: '16px'
  },
  imagePlaceholder: {
    width: '460px',
    height: '315px',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid',
    borderImageSource:
      'linear-gradient(158.03deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.3) 50%, rgba(255, 255, 255, 0.1) 100%)',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(100px)',
    borderRadius: '16px',
    gap: '16px'
  },
  imageIcon: {
    width: '48px',
    height: '48px',
    stroke: 'rgba(255, 255, 255, 1)',
    color: 'rgba(0, 0, 0, 0.3)'
  },
  maxSizeInfo: {
    fontFamily: 'DM Sans',
    fontWeight: 'lighter',
    fontSize: '14px',
    marginTop: '8px',
    color: 'rgba(118, 118, 125, 1)'
  },
  errorText: {
    fontFamily: 'DM Sans',
    fontWeight: 'medium',
    fontSize: '14px',
    marginTop: '8px',
    color: '#d32f2f'
  },
  uploadButton: {
    textTransform: 'none',
    borderRadius: '12px',
    background: 'rgba(97, 35, 255, 1)',
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 1)',
    fontFamily: 'DM Sans',
    padding: '8px 20px'
  },
  imagePreview: {
    maxWidth: `${MAX_IMAGE_WIDTH}px`,
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    borderRadius: '8px',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    boxSizing: 'border-box',
    transition: 'width 0.3s ease, height 0.3s ease'
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: '8px',
    display: 'block'
  },
  deleteButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)'
    }
  }
}
